import { useState, useCallback } from 'react';

export interface ConsumoMensal {
  userId: string;
  mesAno: string; // formato: "YYYY-MM"
  valorTotalResgatado: number;
  quantidadeResgates: number;
}

export interface LimiteMensalConfig {
  limiteTotal: number; // R$ máximo por mês
  valorMinimo: number; // R$ mínimo por resgate
  valorMaximo: number; // R$ máximo por resgate
}

const CONFIG_PADRAO: LimiteMensalConfig = {
  limiteTotal: 3000,
  valorMinimo: 20,
  valorMaximo: 20000,
};

function getMesAnoAtual(): string {
  const agora = new Date();
  const ano = agora.getFullYear();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  return `${ano}-${mes}`;
}

function getChaveStorage(userId: string, tipo: 'pix' | 'pagamento'): string {
  return `limite_mensal_${tipo}_${userId}_${getMesAnoAtual()}`;
}

function carregarConsumo(userId: string, tipo: 'pix' | 'pagamento'): ConsumoMensal {
  const chave = getChaveStorage(userId, tipo);
  try {
    const raw = localStorage.getItem(chave);
    if (raw) {
      return JSON.parse(raw) as ConsumoMensal;
    }
  } catch {
    // ignore
  }
  return {
    userId,
    mesAno: getMesAnoAtual(),
    valorTotalResgatado: 0,
    quantidadeResgates: 0,
  };
}

function salvarConsumo(
  userId: string,
  tipo: 'pix' | 'pagamento',
  consumo: ConsumoMensal
): void {
  const chave = getChaveStorage(userId, tipo);
  try {
    localStorage.setItem(chave, JSON.stringify(consumo));
  } catch {
    // ignore
  }
}

export function useLimiteMensal(
  userId: string,
  tipo: 'pix' | 'pagamento',
  config: LimiteMensalConfig = CONFIG_PADRAO
) {
  const [consumo, setConsumo] = useState<ConsumoMensal>(() =>
    carregarConsumo(userId, tipo)
  );

  const valorDisponivel = Math.max(0, config.limiteTotal - consumo.valorTotalResgatado);
  const percentualUtilizado = Math.min(
    100,
    (consumo.valorTotalResgatado / config.limiteTotal) * 100
  );

  const validarValor = useCallback(
    (valor: number): { valido: boolean; mensagem: string } => {
      if (valor < config.valorMinimo) {
        return {
          valido: false,
          mensagem: `O valor mínimo é R$ ${config.valorMinimo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        };
      }
      if (valor > config.valorMaximo) {
        return {
          valido: false,
          mensagem: `O valor máximo é R$ ${config.valorMaximo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        };
      }
      if (valor > valorDisponivel) {
        return {
          valido: false,
          mensagem: 'Você atingiu o limite mensal disponível para resgates.',
        };
      }
      return { valido: true, mensagem: '' };
    },
    [config.valorMinimo, config.valorMaximo, valorDisponivel]
  );

  const registrarResgate = useCallback(
    (valor: number) => {
      const novoConsumo: ConsumoMensal = {
        ...consumo,
        valorTotalResgatado: consumo.valorTotalResgatado + valor,
        quantidadeResgates: consumo.quantidadeResgates + 1,
      };
      setConsumo(novoConsumo);
      salvarConsumo(userId, tipo, novoConsumo);
    },
    [consumo, userId, tipo]
  );

  return {
    consumo,
    config,
    valorDisponivel,
    percentualUtilizado,
    validarValor,
    registrarResgate,
  };
}
